import type { ApplicantId, ApplicantIncome } from '@/types';

export interface IncomeFormValidation {
  validated: boolean;
  employmentId?: string;
}

export interface IncomeFormHandle {
  update: () => Promise<IncomeFormValidation>;
  scrollToFirstError: () => void;
}

interface ValidateIncomeFormsOptions {
  applicantIds: ApplicantId[];
  forms: IncomeFormHandle[];
  shouldValidate: (applicantId: ApplicantId) => boolean;
}

export type IncomeFormsValidationResult =
  | { validated: true }
  | {
      validated: false;
      applicantId: ApplicantId;
      employmentId?: string;
      form: IncomeFormHandle;
    };

export const shouldValidateIncomeForm = (wasOpened: boolean, income: ApplicantIncome | null): boolean =>
  wasOpened || income !== null;

export const scrollToFirstIncomeError = (container: HTMLElement): void => {
  const errors = Array.from(container.querySelectorAll<HTMLElement>('[data-test-id$="-error"]'));
  const visibleError = errors.find((element) => !element.hidden && element.style.display !== 'none');

  if (visibleError) {
    visibleError.scrollIntoView({ behavior: 'smooth', block: 'center' });
    return;
  }

  container.scrollIntoView({ behavior: 'smooth', block: 'start' });
};

export const validateIncomeForms = async (
  options: ValidateIncomeFormsOptions,
): Promise<IncomeFormsValidationResult> => {
  for (let index = 0; index < options.forms.length; index++) {
    const applicantId = options.applicantIds[index];
    const form = options.forms[index];
    if (!applicantId || !options.shouldValidate(applicantId)) continue;

    const validation = await form.update();
    if (!validation.validated) {
      return {
        validated: false,
        applicantId,
        employmentId: validation.employmentId,
        form,
      };
    }
  }

  return { validated: true };
};
